import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import type { AuthResult } from '@/lib/types';
import { Notice } from './ui';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential?: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
  }
}

const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

/**
 * "Continue with Google" — Google Identity Services returns an ID token, which
 * the adapter forwards to ZenCore's `/auth/google/verify` unchanged.
 *
 * This is SIGN-IN only, and worth keeping distinct from calendar consent in
 * your head: the client id here identifies a person, and the very different
 * Workspace consent later authorises access to the organisation's rooms. Reusing
 * one for the other is a support case that looks like "sign-in asks for calendar
 * permissions".
 *
 * The script is loaded lazily rather than in index.html, so a sign-in page
 * still renders if Google is blocked on the customer's network — the email
 * route above it keeps working.
 */
export function GoogleSignInButton({
  onSignedIn,
  onError,
}: {
  onSignedIn: (result: AuthResult) => void;
  onError: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    if (!clientId) {
      setUnavailable('not-configured');
      return;
    }

    let cancelled = false;

    async function verify(credential: string) {
      try {
        const result = await api<AuthResult>('/auth/google/verify', {
          method: 'POST',
          anonymous: true,
          withoutOrganization: true,
          data: { id_token: credential },
        });
        onSignedIn(result);
      } catch (err) {
        // ZenCore answers 400 when there is no matching user — an admin has to
        // create the account first. That is a different problem from a bad
        // token, and the wording has to make that plain.
        onError(
          err instanceof ApiError && err.status === 400
            ? 'That Google account is not set up in ZenSpace yet. Ask your ZenSpace administrator to add it.'
            : err instanceof ApiError
              ? err.message
              : 'We could not complete Google sign-in. Please try the email option.',
        );
      }
    }

    function render() {
      if (cancelled || !container.current || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId!,
        callback: (response) => {
          if (response.credential) void verify(response.credential);
        },
      });
      window.google.accounts.id.renderButton(container.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: 340,
      });
    }

    if (window.google) {
      render();
      return () => {
        cancelled = true;
      };
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_SCRIPT_SRC}"]`);
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.src = GOOGLE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
    script.addEventListener('load', render);
    script.addEventListener('error', () => {
      if (!cancelled) setUnavailable('unreachable');
    });

    return () => {
      cancelled = true;
      script.removeEventListener('load', render);
    };
  }, [clientId, onSignedIn, onError]);

  if (unavailable === 'not-configured') {
    // Only ever seen by whoever deployed this, and it says what to do.
    return (
      <Notice tone="neutral" title="Google sign-in is not configured">
        Set <code>WEB_GOOGLE_CLIENT_ID</code> before building the admin UI. Email sign-in above is
        unaffected.
      </Notice>
    );
  }

  if (unavailable === 'unreachable') {
    return (
      <Notice tone="neutral" title="Google sign-in could not load">
        Your network may be blocking accounts.google.com. Use the email option above.
      </Notice>
    );
  }

  return <div ref={container} className="flex justify-center" />;
}
